import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import parse from "html-react-parser";

function TypingHTML({ html, duration = 2500 }) {
  const [displayedTextLength, setDisplayedTextLength] = useState(0);

  useEffect(() => {
    if (!html) {
      setDisplayedTextLength(0);
      return;
    }

    const cleanHTML = DOMPurify.sanitize(html);
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = cleanHTML;

    function extractTextNodes(node) {
      let nodes = [];
      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          nodes.push(child.nodeValue);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          nodes = nodes.concat(extractTextNodes(child));
        }
      });
      return nodes;
    }

    const texts = extractTextNodes(tempDiv);
    const totalLength = texts.reduce((sum, t) => sum + t.length, 0);

    if (totalLength === 0) {
      setDisplayedTextLength(0);
      return;
    }

    let startTime = null;

    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;

      const lengthToShow = Math.min(
        totalLength,
        Math.floor((elapsed / duration) * totalLength)
      );

      setDisplayedTextLength(lengthToShow);

      if (elapsed < duration) {
        window.requestAnimationFrame(step);
      } else {
        setDisplayedTextLength(totalLength);
      }
    }

    window.requestAnimationFrame(step);
  }, [html, duration]);

  const parsedNodes = parse(DOMPurify.sanitize(html));

  let tempLength = displayedTextLength;

  function renderNodesWithLimit(nodes) {
    if (!nodes) return null;

    if (typeof nodes === "string") {
      if (tempLength <= 0) return "";
      if (nodes.length <= tempLength) {
        tempLength -= nodes.length;
        return nodes;
      } else {
        const part = nodes.slice(0, tempLength);
        tempLength = 0;
        return part;
      }
    }

    if (Array.isArray(nodes)) {
      return nodes.map((node, i) => (
        <React.Fragment key={i}>{renderNodesWithLimit(node)}</React.Fragment>
      ));
    }

    if (React.isValidElement(nodes)) {
      return React.cloneElement(
        nodes,
        nodes.props,
        renderNodesWithLimit(nodes.props.children)
      );
    }

    return null;
  }

  return <div>{renderNodesWithLimit(parsedNodes)}</div>;
}

export default TypingHTML;
